/**
 * Which farm changes can be made out of sequence with the policy's other changes (#37762).
 *
 * Farm segments after the first are stored as id-matched deltas, each replayed on top of the details before it.
 * Staff and agents can make a change dated before a later change (Robin, CFM: out-of-sequence endorsements must
 * work on farm policies), and policy-mod's SegmentsManager makes the later deltas replay correctly on top of it:
 *   - a data-only change: a later delta only overrides the fields it changed, so it replays as is;
 *   - an added location or risk: the later delta doesn't name it, so it carries forward;
 *   - a removed one: the later deltas are rebased (farm-utils `dropEditsOfRemovedFarmItems`), so a later edit of
 *     the removed item is dropped instead of coming back as a partial risk (the #37762 failure).
 *
 * What stays blocked:
 *   - a change BUILT ON A LATER CHANGE (`built-on-later-change`): its details include a location or risk that
 *     only a later change added. Staff-fe builds a change's form from the change selected in Change History, so
 *     a change dated 10/04 started from the 10/30 change carries 10/30's locations back to 10/04, where they
 *     read as new (a fresh 360Value pull, no coverage defaults). Staff-fe keeps a new change inside the dates of
 *     the change it was started from (`farmNewChangeWindow`); this is the server-side check for what it can see;
 *   - an EDIT whose date moves across a neighbouring change (`moves-before-earlier-change`,
 *     `moves-past-later-change`), which reorders the replay;
 *   - any date that isn't a readable `yyyy-MM-dd` calendar day (`unreadable-date`), rather than letting a
 *     string comparison wave it through.
 *
 * "Change" means a current-term segment after inception. A Renewal Change snapshot (starting after the term end)
 * is excluded, as it is from the current-term timeline everywhere else. Enforced in policy-mod `validateEvent`
 * for `previewPolicyChangeAddSegment` and `previewPolicyChangeToSegment`, and mirrored by staff-fe's change date
 * picker and submit check. Cancellation, reinstatement and rescind run through their own events.
 */
import { isFarmBasedPolicyType } from '@ourbranch/policy-types';

import { currentTermSegmentIndexes } from './current-term-segment-indexes';
import { policyDetailsFromSegments } from './policy-details-from-segments';

export const FARM_CHANGE_OUT_OF_SEQUENCE_ERROR_CODE = 'FARM_CHANGE_OUT_OF_SEQUENCE';

export type FarmChangeSequenceReason =
  | 'built-on-later-change'
  | 'moves-before-earlier-change'
  | 'moves-past-later-change'
  | 'unreadable-date';

export type FarmChangeSequenceCheck =
  | { blocked: false }
  | { blocked: true; reason: FarmChangeSequenceReason; message: string };

type FarmChangeSequencePolicy = {
  policyType?: string | null;
  endDate: string;
  segments?: Array<{ segmentId?: string | null; startDate: string; [field: string]: any }> | null;
};

type FarmRiskLike = { id?: string | null; isDeleted?: boolean | null } | null;
type FarmLocationLike = { id?: string | null; isDeleted?: boolean | null; risks?: FarmRiskLike[] | null } | null;
type FarmDetailsLike = { farmLocations?: FarmLocationLike[] | null } | null | undefined;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The `yyyy-MM-dd` calendar day of `value` (a date or an ISO timestamp), or undefined when unreadable. */
function readIsoDay(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const day = value.slice(0, 10);
  const match = ISO_DAY.exec(day);
  if (!match || (value.length > 10 && value[10] !== 'T')) {
    return undefined;
  }
  const [, year, month, date] = match.map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, date));
  const isCalendarDay =
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === date;
  return isCalendarDay ? day : undefined;
}

const formatUsDate = (isoDay: string) => {
  const [year, month, day] = isoDay.split('-');
  return `${month}/${day}/${year}`;
};

const blocked = (reason: FarmChangeSequenceReason, message: string): FarmChangeSequenceCheck => ({
  blocked: true,
  reason,
  message
});

const unreadable = (value: unknown) =>
  blocked(
    'unreadable-date',
    `This change can't be checked against the policy's other changes because the date ${JSON.stringify(value ?? null)} ` +
      "isn't a valid date (expected yyyy-MM-dd). Choose the change date again; if this keeps happening, escalate to development."
  );

/** The current-term segments after inception, in array order, with their index. */
function currentTermChanges(policy: FarmChangeSequencePolicy) {
  const segments = policy.segments ?? [];
  return currentTermSegmentIndexes({ endDate: policy.endDate, segments })
    .filter((index) => index > 0)
    .map((index) => ({ index, segment: segments[index] }));
}

export type FarmNewChangeWindow = {
  /** The new change must be dated after this day (`yyyy-MM-dd`). */
  after: string;
  /** And before this one, the next change's date, when the change it starts from has a later change. */
  before?: string;
};

/**
 * The dates a NEW farm change started from `fromSegmentId` (the change selected in Change History, whose details
 * fill the form) may carry: after that change and before the next one. A change dated anywhere else would carry
 * the wrong change's details to its date. When the selected segment isn't a current-term change (a Renewal
 * Change, or an id that isn't on the policy) the window starts after the latest change. Undefined for a non-farm
 * policy, a policy with no change after inception, or one whose change dates are unreadable (the submit check
 * then blocks with `unreadable-date`).
 */
export function farmNewChangeWindow({
  policy,
  fromSegmentId
}: {
  policy: FarmChangeSequencePolicy;
  fromSegmentId?: string | null;
}): FarmNewChangeWindow | undefined {
  if (!isFarmBasedPolicyType(policy.policyType) || !currentTermChanges(policy).length) {
    return undefined;
  }
  const segments = policy.segments ?? [];
  const days: Array<{ index: number; day: string }> = [];
  for (const index of currentTermSegmentIndexes({ endDate: policy.endDate, segments })) {
    const day = readIsoDay(segments[index].startDate);
    if (!day) {
      return undefined;
    }
    days.push({ index, day });
  }
  const from = days.find(({ index }) => fromSegmentId && segments[index]?.segmentId === fromSegmentId);
  if (!from) {
    return { after: days.reduce((max, { day }) => (day > max ? day : max), days[0].day) };
  }
  const later = days.filter(({ day }) => day > from.day).map(({ day }) => day);
  return later.length
    ? { after: from.day, before: later.reduce((min, day) => (day < min ? day : min)) }
    : { after: from.day };
}

/** Location ids, and `locationId/riskId` pairs, that are live (not tombstoned) in `details`. Id-less ones are new. */
function liveLocationAndRiskKeys(details: FarmDetailsLike): Set<string> {
  const keys = new Set<string>();
  for (const location of details?.farmLocations ?? []) {
    if (!location?.id || location.isDeleted) {
      continue;
    }
    keys.add(`location:${location.id}`);
    for (const risk of location.risks ?? []) {
      if (risk?.id && !risk.isDeleted) {
        keys.add(`risk:${location.id}/${risk.id}`);
      }
    }
  }
  return keys;
}

/**
 * The date of the earliest later change that added a location or risk `incomingDetails` carries but
 * the details at `baselineIndex` (what the change is built on) don't: evidence the change was started from that
 * later change. Undefined when there is none, or the payload carries no `farmLocations`.
 */
function laterChangeTheDetailsCameFrom({
  policy,
  baselineIndex,
  laterIndexes,
  incomingDetails
}: {
  policy: FarmChangeSequencePolicy;
  baselineIndex: number;
  laterIndexes: number[];
  incomingDetails: FarmDetailsLike;
}): string | undefined {
  if (!Array.isArray(incomingDetails?.farmLocations) || !laterIndexes.length) {
    return undefined;
  }
  const detailsAt = (index: number) =>
    policyDetailsFromSegments(policy as any, { upToSegmentIndex: index }) as unknown as FarmDetailsLike;
  const baseline = liveLocationAndRiskKeys(detailsAt(baselineIndex));
  const added = [...liveLocationAndRiskKeys(incomingDetails)].filter((key) => !baseline.has(key));
  if (!added.length) {
    return undefined;
  }
  for (const index of [...laterIndexes].sort((a, b) => a - b)) {
    const later = liveLocationAndRiskKeys(detailsAt(index));
    if (added.some((key) => later.has(key))) {
      return readIsoDay(policy.segments?.[index]?.startDate);
    }
  }
  return undefined;
}

const builtOnLaterChange = (requested: string, laterChangeDate: string) =>
  blocked(
    'built-on-later-change',
    `This change is dated ${formatUsDate(requested)} but includes a location or risk that the policy's ` +
      `${formatUsDate(laterChangeDate)} change added, so it was started from that later change. Select the ` +
      `change in force on ${formatUsDate(requested)} in Change History and make this change from there.`
  );

/**
 * Decides whether a farm change is in sequence with the policy's other changes (see the file header).
 *
 * - `requestedDate`: the change's date. For an edit, a missing date means the edited change keeps its own.
 * - `editedSegmentId`: the segment an edit targets; omit for a new change. An id that is not on the policy
 *   is judged as a new change.
 * - `incomingDetails`: the details the change submits, checked for a location or risk only a later change added.
 *   `policy.segments` must be the stored segments, since the details before and after the change are rebuilt
 *   from them.
 */
export function checkFarmChangeSequence({
  policy,
  requestedDate,
  editedSegmentId,
  incomingDetails
}: {
  policy: FarmChangeSequencePolicy;
  requestedDate: string | null | undefined;
  editedSegmentId?: string | null;
  incomingDetails?: FarmDetailsLike;
}): FarmChangeSequenceCheck {
  if (!isFarmBasedPolicyType(policy.policyType)) {
    return { blocked: false };
  }
  const segments = policy.segments ?? [];
  const editedIndex = editedSegmentId ? segments.findIndex((segment) => segment?.segmentId === editedSegmentId) : -1;
  // The other current-term changes after inception: these are what the change must stay in sequence with.
  const others = currentTermChanges(policy).filter(({ index }) => index !== editedIndex);
  if (!others.length) {
    return { blocked: false };
  }
  const otherDays: Array<{ index: number; day: string }> = [];
  for (const { index, segment } of others) {
    const start = readIsoDay(segment.startDate);
    if (!start) {
      return unreadable(segment.startDate);
    }
    otherDays.push({ index, day: start });
  }

  if (editedIndex < 0) {
    const requested = readIsoDay(requestedDate);
    if (!requested) {
      return unreadable(requestedDate);
    }
    const inForce = otherDays.filter(({ day }) => day <= requested).map(({ index }) => index);
    const laterChangeDate = laterChangeTheDetailsCameFrom({
      policy,
      baselineIndex: inForce.length ? Math.max(...inForce) : 0,
      laterIndexes: otherDays.filter(({ day }) => day > requested).map(({ index }) => index),
      incomingDetails
    });
    return laterChangeDate ? builtOnLaterChange(requested, laterChangeDate) : { blocked: false };
  }

  const storedDateRaw = segments[editedIndex].startDate;
  const stored = readIsoDay(storedDateRaw);
  if (!stored) {
    return unreadable(storedDateRaw);
  }
  const requested = requestedDate === undefined || requestedDate === null ? stored : readIsoDay(requestedDate);
  if (!requested) {
    return unreadable(requestedDate);
  }
  const earlier = otherDays.filter(({ day }) => day < stored).map(({ day }) => day);
  const later = otherDays.filter(({ day }) => day > stored);
  const previous = earlier.length ? earlier.reduce((max, date) => (date > max ? date : max)) : undefined;
  const next = later.length ? later.map(({ day }) => day).reduce((min, date) => (date < min ? date : min)) : undefined;

  if (previous && requested < previous) {
    return blocked(
      'moves-before-earlier-change',
      `This change can't be moved to ${formatUsDate(requested)}, before the policy's earlier change on ` +
        `${formatUsDate(previous)}. Keep it after ${formatUsDate(previous)}.`
    );
  }
  if (next && requested > next) {
    return blocked(
      'moves-past-later-change',
      `This change can't be moved to ${formatUsDate(requested)}, past the policy's later change on ` +
        `${formatUsDate(next)}. Keep it before ${formatUsDate(next)}, or make a new change dated on or after ` +
        `${formatUsDate(next)}.`
    );
  }
  const laterChangeDate = laterChangeTheDetailsCameFrom({
    policy,
    baselineIndex: editedIndex,
    laterIndexes: later.map(({ index }) => index),
    incomingDetails
  });
  return laterChangeDate ? builtOnLaterChange(requested, laterChangeDate) : { blocked: false };
}

/** Shown when a new farm change's picker range (`farmNewChangeWindow`) leaves no date the user may pick. */
export function farmNoNewChangeAvailableMessage({ after, before }: FarmNewChangeWindow): string {
  if (before) {
    return (
      `A new change started from the ${formatUsDate(after)} change has to be dated after ${formatUsDate(after)} ` +
      `and before the policy's next change on ${formatUsDate(before)}, and no date in between is available here. ` +
      'Select the change in force on the date you want in Change History, or edit this change instead.'
    );
  }
  return (
    `A new change started from the policy's latest change on ${formatUsDate(after)} has to be dated after it, and ` +
    `no later date is available here. Edit the ${formatUsDate(after)} change instead, or, to date the new change ` +
    'earlier, select the change in force on that date in Change History.'
  );
}
